// Libs
import classNames from 'classnames/bind';
import { memo, useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material';
// Components, Layouts, Pages
import Column from './Column';
import Items from './Item';
// Others
import { getFullName } from '~/utils/helper/common';
import { ColumnTypePipeline, ItemChangePipeline, ItemTypePipeline } from '~/utils/interface/pipeline';
// Styles, images, icons
import styles from './BaseDragDrop.module.scss';
import { icons } from '~/assets';

export type BaseDragDropProps = {
  data: any;
  disabled?: boolean;
  onItemChange: (item: ItemChangePipeline) => void;
  onCall: (item: ItemTypePipeline) => void;
  onMail: (item: ItemTypePipeline) => void;
};

const cx = classNames.bind(styles);

function BaseDragDrop(props: BaseDragDropProps) {
  //#region Destructuring Props
  const { data, disabled, onItemChange, onCall, onMail } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/pipeline']);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [columns, setColumns] = useState<ColumnTypePipeline[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (data) {
      setColumns(
        data.map((column: ColumnTypePipeline) => {
          return {
            ...column,
            items: column.items.map((item: ItemTypePipeline) => {
              return { ...item, itemId: item.uuid, uuid: `item-${item.id || 1}` };
            }),
            columnId: column.uuid,
            uuid: `column-${column.id || 1}`,
          };
        })
      );
    }
  }, [data]);
  //#endregion Implement Hook

  //#region Handle Function
  function findValueOfItems(uuid: UniqueIdentifier | undefined, type: string) {
    if (type === 'column') return columns.find((item) => item.uuid === uuid);
    if (type === 'item') return columns.find((column) => column.items.find((item) => item.uuid === uuid));
  }

  const findItem = (uuid: UniqueIdentifier | undefined) => {
    const column = findValueOfItems(uuid, 'item');
    if (!column) return null;
    const item = column.items.find((item) => item.uuid === uuid);
    if (!item) return null;

    return item;
  };

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const { id } = active;
    setActiveId(id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    // Handling item Sorting
    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('item') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      // Find the active and over column
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'item');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item) => item.uuid === active.id);
      const overItemIndex = overContainer.items.findIndex((item) => item.uuid === over.id);
      // In the same column
      let newItems = [...columns];
      if (activeColumnIndex === overColumnIndex) {
        newItems[activeColumnIndex].items = arrayMove(
          newItems[activeColumnIndex].items,
          activeItemIndex,
          overItemIndex
        );

        setColumns(newItems);
      } else {
        // In different columns
        const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
        newItems[overColumnIndex].items.splice(overItemIndex, 0, removeditem);
        setColumns(newItems);
      }

      if (onItemChange) {
        onItemChange({
          columnId: String(overContainer?.id),
          columnPosition: String(overColumnIndex + 1),
          itemId: String(activeId).replace('item-', ''),
          itemPosition: String(overItemIndex + 1),
          data: newItems,
        });
      }
    }

    // Handling item dropping into Column
    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('column') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      // Find the active and over column
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'column');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item) => item.uuid === active.id);

      let newItems = [...columns];
      const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
      newItems[overColumnIndex].items.push(removeditem);
      setColumns(newItems);

      if (onItemChange) {
        onItemChange({
          columnId: String(overContainer?.id),
          columnPosition: String(overColumnIndex + 1),
          itemId: String(activeId).replace('item-', ''),
          itemPosition: String(overColumnIndex + 1),
          data: newItems,
        });
      }
    }

    setActiveId(null);
  }

  const handleGetColorNameColumn = (id: string) => {
    switch (id) {
      case '1':
        return {
          backgroundColor: '#E6F4FF',
          color: '#007AFC',
        };
      case '2':
        return {
          backgroundColor: '#FDEEEB',
          color: '#E8625A',
        };
      case '3':
        return {
          backgroundColor: '#E6F9F4',
          color: '#10A479',
        };
      case '4':
        return {
          backgroundColor: '#E6E6FF',
          color: '#2300FC',
        };
    }
  };

  const renderNameColumn = (column: ColumnTypePipeline) => {
    const style = handleGetColorNameColumn(column.id);
    return (
      <Box className={cx('columnName')} style={style}>
        <Typography className={cx('name')}>{column.name}</Typography>

        <Typography className={cx('total')}>{column.items.length}</Typography>
      </Box>
    );
  };

  const renderItem = (item: ItemTypePipeline) => {
    return (
      <Card className={cx('card')}>
        <CardContent className={cx('cardContent')}>
          <Box className={cx('itemHeader')}>
            <Typography className={cx('itemName')}>
              {getFullName({ firstName: item.firstName, lastName: item.lastName })}
            </Typography>

            <Box className={cx('moreAction')}>
              <IconButton className={cx('action')} onClick={() => onCall?.(item)}>
                <img src={icons.pipelineIconPhone} alt={t('common_alt_img')} />
              </IconButton>

              <IconButton className={cx('action')} onClick={() => onMail?.(item)}>
                <img src={icons.pipelineIconEmail} alt={t('common_alt_img')} />
              </IconButton>
            </Box>
          </Box>

          <Box className={cx('itemFooter')}>
            <Typography className={cx('itemTitleAddress')}>
              {t('card_item_pipeline_title_address')}
            </Typography>

            <Typography className={cx('valueAddress')}>{item.address}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };
  //#endregion Handle Function

  return (
    <Box className={cx('container')}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={columns.map((i) => i.uuid)}>
          <Box className={cx('columns')}>
            {columns.map((column) => (
              <Column id={column.uuid} key={column.uuid} render={renderNameColumn} data={column}>
                <SortableContext items={column.items.map((i) => i.uuid)}>
                  <Box className={cx('column')}>
                    {column.items.map((item) => (
                      <Items
                        data={item}
                        columnData={column}
                        id={item.uuid}
                        key={item.uuid}
                        render={renderItem}
                        disabled={disabled}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </Column>
            ))}
          </Box>
        </SortableContext>
        <DragOverlay adjustScale={false}>
          {activeId && activeId.toString().includes('item') && (
            <Items id={activeId} data={findItem(activeId)} render={renderItem} />
          )}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}

export default memo(BaseDragDrop);
